import * as yup from "yup";
import {
  sanitizeInput,
  hasCSVInjectionChar,
  containsDangerousHtml,
  normalizePhone,
  hasPhoneLeadingForbidden,
  PHONE_DISPLAY_PATTERN,
} from "./validationSecurity";

const csvTest = (label) => ({
  name: "no-csv-injection",
  message: `${label} must not start with +, -, @, or =`,
  test: (value) => !value || !hasCSVInjectionChar(sanitizeInput(value)),
});

const htmlTest = (label) => ({
  name: "no-dangerous-html",
  message: `${label} must not contain HTML tags, scripts, or event handlers`,
  test: (value) => !value || !containsDangerousHtml(value),
});

// eslint-disable-next-line no-useless-escape
const NAME_PATTERN = /^[a-zA-Z\s'\-.]+$/;
const NAME_WITH_AMP_PARENS = /^[a-zA-Z\s'\-.&()]+$/;
const CODE_ALPHANUM_HYPHEN_UNDERSCORE = /^[a-zA-Z0-9\s\-_]+$/;
const CODE_ALPHANUM_ONLY = /^[a-zA-Z0-9]+$/;
const CODE_ALPHANUM_PERIOD_HYPHEN = /^[a-zA-Z0-9.\-]+$/;
const DESCRIPTION_STANDARD = /^[a-zA-Z0-9\s'\-.,;:&/()]+$/;
const DESCRIPTION_WITH_PLUS = /^[a-zA-Z0-9\s'\-.,;:&/()+]+$/;
const FACILITY_NAME_PATTERN = /^[a-zA-Z0-9\s'\-.,&/()]+$/;
const ADDRESS_PATTERN = /^[a-zA-Z0-9\s'\-.,;:&#/()]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const phoneField = (label = "Phone Number") =>
  yup
    .string()
    .required(`${label} is required`)
    .max(20, `${label} must be at most 20 characters`)
    .matches(
      PHONE_DISPLAY_PATTERN,
      `${label} can only contain digits, optional leading +, spaces, hyphens, and parentheses`
    )
    .test({
      name: "phone-leading-forbidden",
      message: `${label} must not start with -, @, or =`,
      test: (value) => {
        if (!value) return true;
        return !hasPhoneLeadingForbidden(sanitizeInput(value));
      },
    })
    .test(htmlTest(label))
    .test({
      name: "phone-normalized",
      message: `${label} must contain digits and normalized value must be at most 20 characters`,
      test: (value) => {
        if (!value || !String(value).trim()) return false;
        const normalized = normalizePhone(value);
        return normalized.length > 0 && normalized.length <= 20;
      },
    });

// ── Point 1: State Master ──
export const stateMasterSchema = yup.object({
  state_Name: yup
    .string()
    .required("State Name is required")
    .max(100, "State Name must be at most 100 characters")
    .matches(
      NAME_WITH_AMP_PARENS,
      "State Name can only contain letters, spaces, apostrophe, hyphen, period, ampersand, and parentheses"
    )
    .test(csvTest("State Name"))
    .test(htmlTest("State Name")),
  state_code: yup
    .string()
    .required("State Code is required")
    .max(20, "State Code must be at most 20 characters")
    .matches(
      /^[a-zA-Z0-9\-_]+$/,
      "State Code can only contain letters, numbers, hyphen, and underscore (no spaces)"
    )
    .test(csvTest("State Code"))
    .test(htmlTest("State Code")),
});

// ── Point 2: Facility Type Master ──
export const facilityTypeMasterSchema = yup.object({
  facility_type_code: yup
    .string()
    .required("Facility Type Code is required")
    .max(20, "Facility Type Code must be at most 20 characters")
    .matches(
      /^[a-zA-Z0-9\-_]+$/,
      "Facility Type Code can only contain letters, numbers, hyphen, and underscore (no spaces)"
    )
    .test(csvTest("Facility Type Code"))
    .test(htmlTest("Facility Type Code")),
  facility_type_Name: yup
    .string()
    .required("Facility Type Name is required")
    .max(100, "Facility Type Name must be at most 100 characters")
    .matches(
      NAME_WITH_AMP_PARENS,
      "Facility Type Name can only contain letters, numbers, spaces, apostrophe, hyphen, period, ampersand, and parentheses"
    )
    .test(csvTest("Facility Type Name"))
    .test(htmlTest("Facility Type Name")),
});

// ── Point 3: CPT Codes (Visit Reason) ──
export const cptCodeSchema = yup.object({
  visit_reason_code: yup
    .string()
    .required("CPT Code is required")
    .max(10, "CPT Code must be at most 10 characters")
    .matches(
      CODE_ALPHANUM_ONLY,
      "CPT Code can only contain letters and numbers (no spaces or special characters)"
    )
    .test(csvTest("CPT Code"))
    .test(htmlTest("CPT Code")),
  visit_reason_description: yup
    .string()
    .required("CPT Description is required")
    .max(255, "CPT Description must be at most 255 characters")
    .matches(
      DESCRIPTION_STANDARD,
      "CPT Description can only contain letters, numbers, spaces, apostrophe, hyphen, period, comma, colon, semicolon, ampersand, forward slash, and parentheses"
    )
    .test(csvTest("CPT Description"))
    .test(htmlTest("CPT Description")),
});

// ── Point 4: DX Codes ──
export const dxCodeSchema = yup.object({
  dx_reason_code: yup
    .string()
    .required("DX Code is required")
    .max(10, "DX Code must be at most 10 characters")
    .matches(
      CODE_ALPHANUM_PERIOD_HYPHEN,
      "DX Code can only contain letters, numbers, period, and hyphen (no spaces)"
    )
    .test(csvTest("DX Code"))
    .test(htmlTest("DX Code")),
  dx_reason_description: yup
    .string()
    .required("DX Description is required")
    .max(255, "DX Description must be at most 255 characters")
    .matches(
      DESCRIPTION_WITH_PLUS,
      "DX Description can only contain letters, numbers, spaces, apostrophe, hyphen, period, comma, colon, semicolon, ampersand, forward slash, parentheses, and plus sign"
    )
    .test(csvTest("DX Description"))
    .test(htmlTest("DX Description")),
});

// ── Shared field builders for user/entity forms ──
const codeField = (label, max = 20) =>
  yup
    .string()
    .required(`${label} is required`)
    .max(max, `${label} must be at most ${max} characters`)
    .matches(
      CODE_ALPHANUM_HYPHEN_UNDERSCORE,
      `${label} can only contain letters, numbers, spaces, hyphen, and underscore`
    )
    .test(csvTest(label))
    .test(htmlTest(label));

const nameField = (label, max = 50) =>
  yup
    .string()
    .required(`${label} is required`)
    .max(max, `${label} must be at most ${max} characters`)
    .matches(
      NAME_PATTERN,
      `${label} can only contain letters, spaces, apostrophe, hyphen, and period`
    )
    .test(csvTest(label))
    .test(htmlTest(label));

const emailField = (label = "Email") =>
  yup
    .string()
    .required(`${label} is required`)
    .max(254, `${label} must be at most 254 characters`)
    .matches(EMAIL_PATTERN, `Please enter a valid email address`)
    .test(csvTest(label))
    .test(htmlTest(label));

const passwordFieldRequired = (label = "Password") =>
  yup
    .string()
    .required(`${label} is required`)
    .max(128, `${label} must be at most 128 characters`)
    .matches(
      PASSWORD_REGEX,
      `${label} must be at least 8 characters long and include one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)`
    );

const passwordFieldOptional = (label = "Password") =>
  yup
    .string()
    .max(128, `${label} must be at most 128 characters`)
    .test(
      "password-strength",
      `${label} must be at least 8 characters long and include one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)`,
      (value) => !value || PASSWORD_REGEX.test(value)
    );

// ── Point 5: Facility ──
export const facilitySchema = yup.object({
  facility_code: codeField("Facility Code", 20),
  facility_name: yup
    .string()
    .required("Facility Name is required")
    .max(100, "Facility Name must be at most 100 characters")
    .matches(
      FACILITY_NAME_PATTERN,
      "Facility Name can only contain letters, numbers, spaces, apostrophe, hyphen, period, comma, ampersand, forward slash, and parentheses"
    )
    .test(csvTest("Facility Name"))
    .test(htmlTest("Facility Name")),
  email: emailField("Email"),
  phone_number: phoneField("Phone Number"),
  facility_address: yup
    .string()
    .required("Facility Address is required")
    .max(250, "Facility Address must be at most 250 characters")
    .matches(
      ADDRESS_PATTERN,
      "Facility Address can only contain letters, numbers, spaces, apostrophe, hyphen, period, comma, colon, semicolon, ampersand, hash, forward slash, and parentheses"
    )
    .test(csvTest("Facility Address"))
    .test(htmlTest("Facility Address")),
});

// ── Point 6: Provider ──
export const providerAddSchema = yup.object({
  providercode: codeField("Provider Code", 20),
  email: emailField("Email"),
  first_name: nameField("Provider First Name", 50),
  last_name: nameField("Provider Last Name", 50),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldRequired("Password"),
});

export const providerEditSchema = yup.object({
  providercode: codeField("Provider Code", 20),
  first_name: nameField("Provider First Name", 50),
  last_name: nameField("Provider Last Name", 50),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldOptional("Password"),
});

// ── Point 7: Employee ──
export const employeeAddSchema = yup.object({
  emp_code: codeField("Employee Code", 20),
  first_name: nameField("Employee First Name", 50),
  last_name: nameField("Employee Last Name", 50),
  email: emailField("Email"),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldRequired("Password"),
});

export const employeeEditSchema = yup.object({
  emp_code: codeField("Employee Code", 20),
  first_name: nameField("Employee First Name", 50),
  last_name: nameField("Employee Last Name", 50),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldOptional("Password"),
});

// ── Point 8: Biller ──
export const billerAddSchema = yup.object({
  billercode: codeField("Biller Code", 20),
  first_name: nameField("Biller First Name", 50),
  last_name: nameField("Biller Last Name", 50),
  email: emailField("Email"),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldRequired("Password"),
});

export const billerEditSchema = yup.object({
  billercode: codeField("Biller Code", 20),
  first_name: nameField("Biller First Name", 50),
  last_name: nameField("Biller Last Name", 50),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldOptional("Password"),
});

// ── Point 9: Admin ──
export const adminAddSchema = yup.object({
  full_name: nameField("Admin Name", 100),
  email: emailField("Email"),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldRequired("Password"),
});

export const adminEditSchema = yup.object({
  full_name: nameField("Admin Name", 100),
  phone_number: phoneField("Phone Number"),
  password: passwordFieldOptional("Password"),
});

// ── Point 10: Timesheet Remarks ──
export const timesheetRemarksSchema = yup.object({
  remarks: yup
    .string()
    .max(1000, "Remarks must be at most 1000 characters")
    .test(csvTest("Remarks"))
    .test(htmlTest("Remarks")),
});

// ── Super Bill row validation (Point 11 -- used without RHF) ──
export const SUPERBILL_FIELD_RULES = {
  patient_name: {
    label: "First Name",
    max: 50,
    pattern: NAME_PATTERN,
    patternMsg:
      "First Name can only contain letters, spaces, apostrophe, hyphen, and period",
  },
  last_name: {
    label: "Last Name",
    max: 50,
    pattern: NAME_PATTERN,
    patternMsg:
      "Last Name can only contain letters, spaces, apostrophe, hyphen, and period",
  },
  modifier: {
    label: "Modifier",
    max: 10,
    pattern: CODE_ALPHANUM_ONLY,
    patternMsg:
      "Modifier can only contain letters and numbers (no spaces or special characters)",
  },
  comment: {
    label: "Comment",
    max: 1000,
    pattern: null,
    patternMsg: null,
  },
};
